import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';



import employeeService from '../../services/employeeService';
import staffIdService from '../../services/staffIdService';
import StaffIdApprovalModal from './components/StaffIdApprovalModal';
import PendingApprovalsPanel from './components/PendingApprovalsPanel';

const StaffIdCardManagementCenter = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCardForApproval, setSelectedCardForApproval] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [employeesData, cardsData, templatesData] = await Promise.all([
        employeeService?.getAll(),
        staffIdService?.getAllCards(),
        staffIdService?.getAllTemplates()
      ]);
      setEmployees(employeesData || []);
      setCards(cardsData || []);
      setTemplates(templatesData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev?.includes(employeeId)) {
        return prev?.filter(id => id !== employeeId);
      }
      return [...prev, employeeId];
    });
  };

  const handleBatchGenerate = async () => {
    if (selectedEmployees?.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const batchId = crypto.randomUUID();
      const issueDate = new Date()?.toISOString()?.split('T')?.[0];
      const expiryDate = new Date(new Date().setFullYear(new Date().getFullYear() + 2))?.toISOString()?.split('T')?.[0];

      const cardsToCreate = selectedEmployees?.map(employeeId => {
        const employee = employees?.find(e => e?.id === employeeId);
        return {
          employeeId: employeeId,
          templateId: selectedTemplate,
          cardNumber: `ID-${employee?.employeeNumber || employee?.staffNumber || Date.now()}`,
          issueDate: issueDate,
          expiryDate: expiryDate,
          status: 'draft',
          qrCodeData: JSON.stringify({
            employeeId: employeeId,
            cardNumber: `ID-${employee?.employeeNumber}`,
            issueDate: issueDate
          }),
          createdBy: 'current_user'
        };
      });

      const createdCards = await staffIdService?.batchCreateCards(cardsToCreate);

      // Submit cards for approval workflow
      const approvalPromises = createdCards?.map(card => 
        staffIdService?.submitStaffIdForApproval(
          card?.id,
          card?.employeeId,
          card?.templateId,
          'current_user'
        )
      );

      await Promise.all(approvalPromises);

      // Create generation logs
      await Promise.all(
        createdCards?.map(card =>
          staffIdService?.createGenerationLog({
            cardId: card?.id,
            actionType: 'created',
            performedBy: 'current_user',
            batchId: batchId,
            success: true,
            metadata: { batchSize: selectedEmployees?.length, submittedForApproval: true }
          })
        )
      );

      setSuccess(`Successfully generated ${createdCards?.length} ID cards and submitted for approval`);
      setSelectedEmployees([]);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to generate ID cards');
    } finally {
      setLoading(false);
    }
  };

  const handleViewApprovalDetails = (cardId) => {
    setSelectedCardForApproval(cardId);
    setShowApprovalModal(true);
  };

  const handleResubmitForApproval = async (cardId) => {
    try {
      setLoading(true);
      setError('');
      await staffIdService?.resubmitStaffIdForApproval(
        cardId,
        'current_user',
        'Resubmitted after addressing rejection feedback'
      );
      setSuccess('Card resubmitted for approval successfully');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to resubmit card');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees?.filter(emp =>
    emp?.firstName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    emp?.lastName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    emp?.employeeNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    emp?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  const employeesWithCards = employees?.filter(emp =>
    cards?.some(card => card?.employeeId === emp?.id)
  ) || [];

  const employeesWithoutCards = employees?.filter(emp =>
    !cards?.some(card => card?.employeeId === emp?.id)
  ) || [];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Staff ID Card Management Center
          </h1>
          <p className="text-muted-foreground">
            Generate, manage, and approve employee identification cards with multi-level approval workflow
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="CheckCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Users" size={20} className="text-primary" />
              <span className="text-2xl font-bold text-foreground">{employees?.length || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Employees</p>
          </div>

          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon name="CreditCard" size={20} className="text-success" />
              <span className="text-2xl font-bold text-foreground">{cards?.length || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Active ID Cards</p>
          </div>

          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Clock" size={20} className="text-warning" />
              <span className="text-2xl font-bold text-foreground">
                {cards?.filter(c => c?.approvalStatus === 'pending_approval' || c?.approvalStatus === 'in_progress')?.length || 0}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Pending Approval</p>
          </div>

          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon name="UserX" size={20} className="text-error" />
              <span className="text-2xl font-bold text-foreground">{employeesWithoutCards?.length || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Pending Cards</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-lg border border-border">
          <div className="border-b border-border">
            <div className="flex gap-4 px-6">
              <button
                onClick={() => setActiveTab('employees')}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === 'employees' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Users" size={18} className="inline mr-2" />
                Employee Database
              </button>
              <button
                onClick={() => setActiveTab('batch')}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === 'batch' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Layers" size={18} className="inline mr-2" />
                Batch Generation
              </button>
              <button
                onClick={() => setActiveTab('approvals')}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === 'approvals' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="CheckSquare" size={18} className="inline mr-2" />
                Approval Status
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === 'pending' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Clock" size={18} className="inline mr-2" />
                Pending Actions
              </button>
              <button
                onClick={() => setActiveTab('cards')}
                className={`py-4 px-2 border-b-2 transition-colors ${
                  activeTab === 'cards' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="CreditCard" size={18} className="inline mr-2" />
                ID Cards
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Pending Actions Tab */}
            {activeTab === 'pending' && <PendingApprovalsPanel />}

            {/* Approval Status Tab */}
            {activeTab === 'approvals' && (
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Approval Status</h3>
                  <p className="text-sm text-muted-foreground">Track staff ID card approval workflow</p>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cards?.filter(card => card?.approvalStatus)?.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Icon name="FileCheck" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No cards in approval workflow</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Card Number</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Submitted</th>
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cards?.filter(card => card?.approvalStatus)?.map(card => (
                              <tr key={card?.id} className="border-b border-border hover:bg-muted/50">
                                <td className="py-3 px-4 text-sm text-foreground">{card?.cardNumber}</td>
                                <td className="py-3 px-4 text-sm text-foreground">
                                  {card?.employee?.firstName} {card?.employee?.lastName}
                                </td>
                                <td className="py-3 px-4 text-sm text-foreground">{card?.employee?.department}</td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    card?.approvalStatus === 'approved' ? 'bg-success/10 text-success' :
                                    card?.approvalStatus === 'rejected' ? 'bg-error/10 text-error' :
                                    card?.approvalStatus === 'in_progress'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                                  }`}>
                                    {card?.approvalStatus === 'approved' && <Icon name="CheckCircle" size={14} className="mr-1" />}
                                    {card?.approvalStatus === 'rejected' && <Icon name="XCircle" size={14} className="mr-1" />}
                                    {card?.approvalStatus === 'in_progress' && <Icon name="Clock" size={14} className="mr-1" />}
                                    {card?.approvalStatus === 'pending_approval' && <Icon name="AlertCircle" size={14} className="mr-1" />}
                                    {card?.approvalStatus?.replace('_', ' ')?.toUpperCase()}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-sm text-muted-foreground">
                                  {card?.submittedForApprovalAt ? new Date(card?.submittedForApprovalAt)?.toLocaleDateString() : '-'}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleViewApprovalDetails(card?.id)}
                                      className="text-primary hover:text-primary/80 text-sm flex items-center gap-1"
                                    >
                                      <Icon name="Eye" size={16} />
                                      View Details
                                    </button>
                                    {card?.approvalStatus === 'rejected' && (
                                      <button
                                        onClick={() => handleResubmitForApproval(card?.id)}
                                        className="text-warning hover:text-warning/80 text-sm flex items-center gap-1"
                                      >
                                        <Icon name="RefreshCw" size={16} />
                                        Resubmit
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Other existing tabs remain unchanged */}
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedCardForApproval && (
        <StaffIdApprovalModal
          cardId={selectedCardForApproval}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedCardForApproval(null);
          }}
          onApprovalComplete={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default StaffIdCardManagementCenter;